import React from 'react';
import * as Sentry from '@sentry/react';
import { translate } from '../translate';

/**
 * Error boundary specifically for handling chunk loading errors.
 * When a chunk fails to load (usually due to a new deployment), this component
 * will clear the cache and reload the page to fetch the new chunks.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a chunk loading error
    if (
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk')
    ) {
      return { hasError: true, isReloading: true };
    }
    // For other errors, let the parent error boundary handle them
    return null;
  }

  componentDidCatch(error, errorInfo) {
    // Only handle chunk loading errors
    if (
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk')
    ) {
      // Log to Sentry with additional context
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('error_type', 'chunk_load_error');
        scope.setContext('chunk_error', {
          error_name: error?.name,
          error_message: error?.message,
          chunk_url: error?.request || 'unknown',
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });
        scope.setFingerprint(['chunk-load-error', error?.message]);
        Sentry.captureException(error, {
          tags: {
            component: 'ChunkErrorBoundary',
            handled: true,
          },
        });
      });

      // Clear service worker caches if available
      if (window.caches) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }

      // Clear localStorage items related to app state if needed
      // (be careful not to clear user authentication tokens)
      try {
        const keysToPreserve = ['authToken', 'user', 'token']; // Adjust as needed
        const allKeys = Object.keys(localStorage);
        allKeys.forEach((key) => {
          if (!keysToPreserve.some((preserve) => key.includes(preserve))) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        // Silently fail if localStorage clearing fails
      }

      // Reload the page after a short delay to allow cache clearing
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  render() {
    if (this.state.hasError && this.state.isReloading) {
      // Get language from localStorage for translation
      const savedLanguage = localStorage.getItem('languageV2') || 'en';
      const loadingText = translate('loadingNewVersion', savedLanguage);

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
          }}
        >
          <div
            style={{
              fontSize: '18px',
              marginBottom: '10px',
              color: '#333',
            }}
          >
            {loadingText}
          </div>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #5D87FF',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;

